import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDebouncedSearch } from '../../hooks/useDebouncedSearch';
import api from '../../services/api';
import './BridgeClubPages.css';

interface Purchase {
  id: number;
  purchase_date: string;
  member_name: string;
  member_email: string;
  store_name: string;
  item_count: number;
  total: string;
  red_bridge_bags: number;
  verification_status: string;
  receipt_image: string | null;
  items: Array<{
    product_name: string;
    quantity: number;
    price: string;
    is_red_bridge: boolean;
  }>;
}

interface PurchasesResponse {
  purchases: Purchase[];
  total_count: number;
  total_revenue: number;
  avg_purchase: number;
}

const BridgeClubPurchases: React.FC = () => {
  const navigate = useNavigate();
  const [purchases, setPurchases] = useState<Purchase[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [totalCount, setTotalCount] = useState(0);
  const [totalRevenue, setTotalRevenue] = useState(0);
  const [avgPurchase, setAvgPurchase] = useState(0);

  const [filters, setFilters] = useState({
    startDate: '',
    endDate: '',
    storeId: '',
    search: '',
    status: ''
  });

  const [selectedPurchase, setSelectedPurchase] = useState<Purchase | null>(null);

  // Server search: the input holds instant local state; the debounced value (400ms) is
  // synced into filters.search, so the [filters] fetch fires once after the user pauses.
  const { value: searchInput, setValue: setSearchInput, debounced: debouncedSearch } = useDebouncedSearch('', 400);
  useEffect(() => {
    setFilters(f => (f.search === debouncedSearch ? f : { ...f, search: debouncedSearch }));
  }, [debouncedSearch]);

  const fetchPurchases = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      if (filters.startDate) params.append('startDate', filters.startDate);
      if (filters.endDate) params.append('endDate', filters.endDate);
      if (filters.search) params.append('search', filters.search);
      if (filters.status) params.append('status', filters.status);

      const response = await api.get<PurchasesResponse>(`/admin/bridge-club/purchases/?${params.toString()}`);
      setPurchases(response.data.purchases || []);
      setTotalCount(response.data.total_count || 0);
      setTotalRevenue(response.data.total_revenue || 0);
      setAvgPurchase(response.data.avg_purchase || 0);
    } catch (err: any) {
      console.error('Failed to load purchases:', err);
      setError(err.response?.data?.detail || 'Failed to load purchases');
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    fetchPurchases();
  }, [fetchPurchases]);

  const formatCurrency = (amount: number | string) => {
    const num = typeof amount === 'string' ? parseFloat(amount) : amount;
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(num);
  };

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getStatusBadge = (status: string) => {
    const statusMap: Record<string, { label: string; className: string }> = {
      'auto_verified': { label: 'Auto-Verified', className: 'badge-success' },
      'verified': { label: 'Verified', className: 'badge-success' },
      'pending': { label: 'Pending', className: 'badge-warning' },
      'rejected': { label: 'Rejected', className: 'badge-danger' }
    };
    const { label, className } = statusMap[status] || { label: status, className: 'badge-neutral' };
    return <span className={`status-badge ${className}`}>{label}</span>;
  };

  const exportToCSV = () => {
    const headers = ['Date', 'Member', 'Email', 'Store', 'Items', 'Total', 'Red Bridge Bags', 'Status'];
    const rows = purchases.map(p => [
      p.purchase_date,
      p.member_name,
      p.member_email,
      p.store_name,
      p.item_count,
      p.total,
      p.red_bridge_bags,
      p.verification_status
    ]);

    const csvContent = [headers, ...rows].map(row => row.join(',')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `bridge-club-purchases-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
  };

  if (loading && purchases.length === 0) {
    return (
      <div className="bridge-club-page">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading purchases...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bridge-club-page">
      <div className="page-header">
        <div className="header-content">
          <button className="back-button" onClick={() => navigate('/admin/bridge-club')}>
            &larr; Back to Bridge Club
          </button>
          <h1>Bridge Club Purchases</h1>
          <p>All purchases made by Bridge Club members</p>
        </div>
      </div>

      {error && (
        <div className="error-banner">
          {error}
          <button onClick={fetchPurchases}>Retry</button>
        </div>
      )}

      {/* Summary Stats */}
      <div className="stats-row">
        <div className="stat-card">
          <div className="stat-value">{totalCount.toLocaleString()}</div>
          <div className="stat-label">Total Purchases</div>
        </div>
        <div className="stat-card">
          <div className="stat-value">{formatCurrency(totalRevenue)}</div>
          <div className="stat-label">Total Revenue</div>
        </div>
        <div className="stat-card">
          <div className="stat-value">{formatCurrency(avgPurchase)}</div>
          <div className="stat-label">Avg Purchase</div>
        </div>
        <div className="stat-card">
          <div className="stat-value">
            {purchases.reduce((sum, p) => sum + p.red_bridge_bags, 0).toLocaleString()}
          </div>
          <div className="stat-label">Red Bridge Bags</div>
        </div>
      </div>

      {/* Filters */}
      <div className="filters-row">
        <input
          type="date"
          value={filters.startDate}
          onChange={e => setFilters({ ...filters, startDate: e.target.value })}
          placeholder="Start Date"
        />
        <input
          type="date"
          value={filters.endDate}
          onChange={e => setFilters({ ...filters, endDate: e.target.value })}
          placeholder="End Date"
        />
        <input
          type="text"
          value={searchInput}
          onChange={e => setSearchInput(e.target.value)}
          placeholder="Search member or product..."
          className="search-input"
        />
        <select
          value={filters.status}
          onChange={e => setFilters({ ...filters, status: e.target.value })}
        >
          <option value="">All Statuses</option>
          <option value="verified">Verified</option>
          <option value="auto_verified">Auto-Verified</option>
          <option value="pending">Pending</option>
          <option value="rejected">Rejected</option>
        </select>
        <button className="btn-export" onClick={exportToCSV}>
          Export CSV
        </button>
      </div>

      {/* Purchases Table */}
      <div className="table-container">
        <table className="data-table">
          <thead>
            <tr>
              <th>Date</th>
              <th>Member</th>
              <th>Store</th>
              <th>Items</th>
              <th>Total</th>
              <th>RB Bags</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            {purchases.length === 0 ? (
              <tr>
                <td colSpan={7} className="empty-row">
                  No purchases found
                </td>
              </tr>
            ) : (
              purchases.map(purchase => (
                <tr key={purchase.id} onClick={() => setSelectedPurchase(purchase)}>
                  <td>{formatDate(purchase.purchase_date)}</td>
                  <td>
                    <div className="member-info">
                      <span className="member-name">{purchase.member_name}</span>
                      <span className="member-email">{purchase.member_email}</span>
                    </div>
                  </td>
                  <td>{purchase.store_name}</td>
                  <td>{purchase.item_count} items</td>
                  <td className="currency">{formatCurrency(purchase.total)}</td>
                  <td className="center">{purchase.red_bridge_bags}</td>
                  <td>{getStatusBadge(purchase.verification_status)}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Purchase Detail Modal */}
      {selectedPurchase && (
        <div className="modal-overlay" onClick={() => setSelectedPurchase(null)}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Purchase Details</h2>
              <button className="modal-close" onClick={() => setSelectedPurchase(null)}>×</button>
            </div>
            <div className="modal-body">
              <div className="detail-grid">
                <div className="detail-item">
                  <label>Member</label>
                  <span>{selectedPurchase.member_name}</span>
                </div>
                <div className="detail-item">
                  <label>Email</label>
                  <span>{selectedPurchase.member_email}</span>
                </div>
                <div className="detail-item">
                  <label>Store</label>
                  <span>{selectedPurchase.store_name}</span>
                </div>
                <div className="detail-item">
                  <label>Date</label>
                  <span>{formatDate(selectedPurchase.purchase_date)}</span>
                </div>
                <div className="detail-item">
                  <label>Total</label>
                  <span className="total-amount">{formatCurrency(selectedPurchase.total)}</span>
                </div>
                <div className="detail-item">
                  <label>Status</label>
                  {getStatusBadge(selectedPurchase.verification_status)}
                </div>
              </div>

              {selectedPurchase.items && selectedPurchase.items.length > 0 && (
                <div className="items-section">
                  <h3>Items</h3>
                  <table className="items-table">
                    <thead>
                      <tr>
                        <th>Product</th>
                        <th>Qty</th>
                        <th>Price</th>
                        <th>Red Bridge</th>
                      </tr>
                    </thead>
                    <tbody>
                      {selectedPurchase.items.map((item, idx) => (
                        <tr key={idx}>
                          <td>{item.product_name}</td>
                          <td>{item.quantity}</td>
                          <td>{formatCurrency(item.price)}</td>
                          <td>{item.is_red_bridge ? 'Yes' : 'No'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {selectedPurchase.receipt_image && (
                <div className="receipt-section">
                  <h3>Receipt</h3>
                  <img src={selectedPurchase.receipt_image} alt="Receipt" className="receipt-image" />
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BridgeClubPurchases;
